import { Component } from '@theme/component';

const HISTORY_KEY = 'mvProductZoom';

/**
 * Adds reversible browser-history and explicit focus-return behavior around
 * Horizon's zoom-dialog component.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog.js').ZoomDialog} zoomDialogComponent
 *
 * @extends {Component<Refs>}
 */
class MvProductZoom extends Component {
  requiredRefs = ['zoomDialogComponent'];

  /** @type {HTMLElement | null} */
  #trigger = null;

  #historyActive = false;

  connectedCallback() {
    super.connectedCallback();
    this.#dialog?.addEventListener('close', this.#handleDialogClose);
    window.addEventListener('popstate', this.#handlePopState);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#dialog?.removeEventListener('close', this.#handleDialogClose);
    window.removeEventListener('popstate', this.#handlePopState);

    if (this.#matchesHistoryState(history.state)) {
      this.#replaceHistoryStateWithoutMarker();
    }
  }

  /** @returns {HTMLDialogElement | null} */
  get #dialog() {
    const dialog = this.refs.zoomDialogComponent?.refs?.dialog;
    return dialog instanceof HTMLDialogElement ? dialog : null;
  }

  /** @returns {string} */
  get #historyToken() {
    return this.dataset.historyToken || this.id;
  }

  /**
   * Opens a selected image through Horizon's zoom component.
   *
   * @param {number} index
   * @param {Event} event
   */
  async open(index, event) {
    const dialog = this.#dialog;
    if (!dialog || dialog.open) return;

    this.#trigger = event.target instanceof HTMLElement ? event.target.closest('button, a') : null;
    this.#pushHistoryState(index);

    try {
      await this.refs.zoomDialogComponent.open(index, event);
      await this.refs.zoomDialogComponent.selectThumbnail(index, { behavior: 'instant' });

      const media = this.refs.zoomDialogComponent.refs.media[index];
      if (media) this.refs.zoomDialogComponent.loadHighResolutionImage(media);
    } catch (error) {
      if (this.#historyActive) history.back();
      this.#historyActive = false;
      throw error;
    }
  }

  /** @param {number} index */
  #pushHistoryState(index) {
    if (this.#matchesHistoryState(history.state)) {
      this.#historyActive = true;
      return;
    }

    try {
      const currentState = history.state && typeof history.state === 'object' ? history.state : {};
      history.pushState(
        {
          ...currentState,
          [HISTORY_KEY]: this.#historyToken,
          mvProductZoomIndex: index,
        },
        '',
        window.location.href
      );
      this.#historyActive = true;
    } catch {
      this.#historyActive = false;
    }
  }

  /** @param {unknown} state */
  #matchesHistoryState(state) {
    return Boolean(
      state &&
        typeof state === 'object' &&
        HISTORY_KEY in state &&
        /** @type {Record<string, unknown>} */ (state)[HISTORY_KEY] === this.#historyToken
    );
  }

  #replaceHistoryStateWithoutMarker() {
    const currentState = history.state && typeof history.state === 'object' ? { ...history.state } : {};
    delete currentState[HISTORY_KEY];
    delete currentState.mvProductZoomIndex;
    history.replaceState(currentState, '', window.location.href);
  }

  #handleDialogClose = () => {
    const shouldConsumeHistory = this.#historyActive && this.#matchesHistoryState(history.state);
    this.#historyActive = false;

    if (shouldConsumeHistory) history.back();

    queueMicrotask(() => {
      if (this.#trigger?.isConnected) this.#trigger.focus({ preventScroll: true });
    });
  };

  /** @param {PopStateEvent} event */
  #handlePopState = async (event) => {
    const dialog = this.#dialog;
    if (!dialog) return;

    if (this.#matchesHistoryState(event.state)) {
      if (dialog.open) return;

      const state = /** @type {Record<string, unknown>} */ (event.state);
      const requestedIndex = Number(state.mvProductZoomIndex);
      const index = Number.isInteger(requestedIndex) && requestedIndex >= 0 ? requestedIndex : 0;

      this.#historyActive = true;
      dialog.showModal();
      await this.refs.zoomDialogComponent.selectThumbnail(index, { behavior: 'instant' });

      const media = this.refs.zoomDialogComponent.refs.media[index];
      if (media) this.refs.zoomDialogComponent.loadHighResolutionImage(media);
      return;
    }

    if (!this.#historyActive || !dialog.open) return;

    this.#historyActive = false;
    await this.refs.zoomDialogComponent.close();
  };
}

if (!customElements.get('mv-product-zoom')) {
  customElements.define('mv-product-zoom', MvProductZoom);
}
